import ApiService from '@/service/ApiService';
import { useAuthStore } from '@/store/auth-store';
import { useReservationStore } from '@/store/reservation-store';
import { defineStore } from 'pinia';
import { computed, reactive, watch } from 'vue';

export const useReservationCartStore = defineStore(
    'reservationCart',
    () => {
        const cart = reactive({
            reservation_no: '',
            expires_at: '',
            collision_insurance_cost: 0,
            delivery_cost: 0,
            rental_price: 0,
            total_amount: 0,
            net_amount: 0,
            vehicle_group_id: 0,
            vehicle_group_name: '',
            vehicle_group_image: '',
            reservation_date: '',
            reservation_time: '',
            customer_name: '',
            phone: '',
            corporation_name: '',
            collision_insurance_state: '',
            start_date: '',
            start_time: '',
            end_date: '',
            end_time: '',
            garage_name: '',
            pickup_address: '',
            pickup_address_detail: '',
            return_address: '',
            return_address_detail: '',
            email: '',
            is_virtual_account: false,
            account_number: '',
            bank_code: '',
            due_date: ''
        });

        let expireTimer = null;

        function applyCartData(data) {
            if (data.reservation_no) cart.reservation_no = data.reservation_no;
            if (data.reservation_date) cart.reservation_date = data.reservation_date;
            if (data.reservation_time) cart.reservation_time = data.reservation_time;
            cart.vehicle_group_id = data.vehicle_group_id;
            cart.vehicle_group_name = data.vehicle_group_name;
            cart.vehicle_group_image = data.vehicle_group_image;
            cart.collision_insurance_cost = data.collision_insurance_cost;
            cart.collision_insurance_state = data.collision_insurance_state;
            cart.delivery_cost = data.delivery_cost;
            cart.rental_price = data.rental_price;
            cart.total_amount = data.total_amount;
            cart.net_amount = data.net_amount;
            cart.customer_name = data.customer_name;
            cart.phone = data.phone;
            cart.corporation_name = data.corporation_name;
            cart.start_date = data.start_date;
            cart.start_time = data.start_time;
            cart.end_date = data.end_date;
            cart.end_time = data.end_time;
            cart.garage_name = data.garage_name;
            cart.pickup_address = data.pickup_address;
            cart.pickup_address_detail = data.pickup_address_detail;
            cart.return_address = data.return_address;
            cart.return_address_detail = data.return_address_detail;
            cart.is_virtual_account = data.is_virtual_account;
            cart.account_number = data.account_number;
            cart.bank_code = data.bank_code;
            cart.due_date = data.due_date;
            cart.email = data.email || '';

            if (data.is_virtual_account) {
                // due_date  만료시간
                // "YYYY-MM-DD HH:mm" → "YYYY-MM-DDTHH:mm:00"
                const iso = data.due_date.replace(' ', 'T') + ':00';
                cart.expires_at = new Date(iso).toISOString();
            } else {
                // 예약시간 + 30분
                const expireDate = new Date(`${data.reservation_date}T${data.reservation_time}:00`);
                cart.expires_at = new Date(expireDate.getTime() + 30 * 60 * 1000).toISOString();
            }
        }

        async function fetchCart() {
            const authStore = useAuthStore();
            const isLoggedIn = computed(() => !!authStore.userId); // 로그인여부
            //const reservationStore = useReservationStore();
            try {
                let response;
                if (authStore.userId) {
                    response = await ApiService.get('/api/customer/member/cart');
                } else {
                    //const fallbackReservationNo = reservationStore.reservationData.reservation_no;
                    const reservationNo = cart.reservation_no; // || fallbackReservationNo;

                    if (!reservationNo) return;
                    response = await ApiService.post('/api/customer/cart/detail', {
                        reservation_no: reservationNo
                    });
                }

                const data = response.data.data;

                if (!data?.reservation_no) {
                    clearCart();
                    return;
                }

                applyCartData({
                    reservation_no: data.reservation_no,
                    reservation_date: data.reservation_date,
                    reservation_time: data.reservation_time,
                    vehicle_group_id: data.vehicle_group_id,
                    vehicle_group_name: data.vehicle_group_name,
                    vehicle_group_image: data.vehicle_group_image,
                    collision_insurance_cost: data.collision_insurance_cost,
                    collision_insurance_state: data.collision_insurance_state,
                    delivery_cost: data.delivery_cost,
                    rental_price: data.rental_price,
                    total_amount: data.total_amount,
                    net_amount: data.net_amount,
                    customer_name: data.customer_name,
                    phone: data.phone,
                    corporation_name: data.corporation_name,
                    start_date: data.start_date,
                    start_time: data.start_time,
                    end_date: data.end_date,
                    end_time: data.end_time,
                    garage_name: data.garage_name,
                    pickup_address: data.pickup_address,
                    pickup_address_detail: data.pickup_address_detail,
                    return_address: data.return_address,
                    return_address_detail: data.return_address_detail,
                    is_virtual_account: data.is_virtual_account,
                    bank_code: data.bank_code,
                    account_number: data.account_number,
                    due_date: data.due_date,
                    email: isLoggedIn.value === true ? authStore.userId : ''
                });

                console.log(cart);
            } catch (err) {
                if (err.response.data.code === 'NOT_FOUND_CONTRACT_NO') console.error(err.response.data?.msg);
                else console.error('장바구니 조회 실패', err);
                clearCart();
            }
        }

        // function addToCart({ reservation_no, reservation_date, reservation_time, vehicle_group_name, total_amount }) {
        //     cart.reservation_no = reservation_no;
        //     cart.vehicle_group_name = vehicle_group_name || '';
        //     cart.total_amount = total_amount || 0;
        //     const base = new Date(`${reservation_date}T${reservation_time}:00`);
        //     cart.expires_at = new Date(base.getTime() + 30 * 60 * 1000).toISOString();

        //     const reservationStore = useReservationStore();
        //     reservationStore.resetReservationData();
        // }

        function clearCart() {
            if (expireTimer) {
                clearTimeout(expireTimer);
                expireTimer = null;
            }

            Object.keys(cart).forEach((key) => {
                if (typeof cart[key] === 'string') cart[key] = '';
                else cart[key] = 0;
            });
        }

        // function expireAll() {
        //     clearCart();
        //     const reservationStore = useReservationStore();
        //     const authStore = useAuthStore();

        //     if (authStore.userId) {
        //         reservationStore.updateReservationData({
        //             customer_login_id: authStore.userId,
        //             customer_name: authStore.userName,
        //             birthdate: authStore.birthDate,
        //             phone: authStore.mobile,
        //             customer_type: authStore.customerType,
        //             corporation_name: authStore.corporationName,
        //             driver_name: authStore.userName,
        //             driver_phone: authStore.mobile,
        //             driver_birth_date: authStore.birthDate.replaceAll('-', '')
        //         });
        //     } else {
        //         reservationStore.resetReservationData();
        //     }
        // }

        function scheduleExpire() {
            if (expireTimer) {
                clearTimeout(expireTimer);
                expireTimer = null;
            }
            if (!cart.expires_at) return;

            const ms = new Date(cart.expires_at).getTime() - Date.now();
            if (ms <= 0) {
                clearCart();
            } else {
                expireTimer = setTimeout(clearCart, ms);
            }
        }

        // expires_at 이 변경될 때마다 (세션 복원 포함) 스케줄 재설정
        watch(() => cart.expires_at, scheduleExpire, { immediate: true });

        const authStore = useAuthStore();
        const reservationStore = useReservationStore();

        // watch(
        //     () => authStore.userId,
        //     async (uid, prevUid) => {
        //         if (uid && !prevUid) {
        //             // 비회원 → 로그인
        //             clearCart(); // 비회원 장바구니 초기화
        //             //reservationStore.resetReservationData(); // 비회원 예약정보 초기화
        //             await fetchCart(); // 로그인한 유저의 장바구니 불러오기
        //         } else if (!uid && prevUid) {
        //             // 로그인 → 로그아웃
        //             clearCart(); // 장바구니 초기화
        //             //reservationStore.resetReservationData(); // 예약정보 초기화
        //         }
        //     },
        //     { immediate: false }
        // );

        const isCartValid = computed(() => !!cart.reservation_no && cart.expires_at && new Date() < new Date(cart.expires_at));
        const cartCount = computed(() => (isCartValid.value ? 1 : 0));
        const minutesLeft = computed(() => {
            if (!cart.expires_at) return 0;
            const diffMs = new Date(cart.expires_at).getTime() - Date.now();
            return Math.ceil(Math.max(diffMs, 0) / 60000);
        });

        return {
            cart,
            isCartValid,
            cartCount,
            minutesLeft,
            applyCartData,
            clearCart,
            fetchCart
        };
    },
    {
        persist: {
            storage: sessionStorage,
            paths: ['cart.reservation_no', 'cart.expires_at', 'cart.total_amount', 'cart.vehicle_group_name']
            // afterRestore({ store }) {
            //     if (store.cart.expires_at && new Date() >= new Date(store.cart.expires_at)) {
            //         store.fetchCart();
            //     }
            // }
        }
    }
);
